/* assets/gproperty-public-gateway.js — GPROPERTY-PUBLIC-GATEWAY-04
 * Transport adapter for Shopify Web / PWA public GProperty requests.
 * Canonical browser transport is https://api.glhksolution.com/property/*.
 * gcity-platform-router remains the backend routing authority on the custom domain;
 * frontend code must not depend on Shopify App Proxy or workers.dev hosts.
 * Storage, draft normalization, readiness semantics and fail-closed recognition
 * remain owned by assets/gproperty-flow-context.js.
 */
(function (window, document) {
  'use strict';

  var VERSION = 'gproperty-public-gateway-04';
  var API_ORIGIN = 'https://api.glhksolution.com';
  var PROPERTY_ROUTE_PREFIX = '/property/';
  var LOCATION_PROVENANCE_CONTRACT = 'gproperty_real_location_provenance_v1';

  /* ──────────────────────────────────────────────────────────────────────────
   * 1. ROUTE SANITIZATION & SECURITY CHECKS
   * ────────────────────────────────────────────────────────────────────────── */
  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function isPropertyRoute(pathname) {
    return String(pathname || '').indexOf(PROPERTY_ROUTE_PREFIX) === 0;
  }

  function unsafeEndpoint(url) {
    return !!(
      url.searchParams.get('deep') === '1' ||
      url.pathname.indexOf('/family-data/ingest') > -1 ||
      url.pathname.indexOf('/gproperty/leads') > -1
    );
  }

  function resolvePublicEndpoint(endpoint) {
    var raw = clean(endpoint);
    var url;

    if (!raw) {
      throw new Error('public_gateway_endpoint_missing');
    }

    try {
      url = new URL(raw, API_ORIGIN);
    } catch (_) {
      throw new Error('public_gateway_endpoint_invalid');
    }

    if (unsafeEndpoint(url)) {
      throw new Error('public_gateway_endpoint_forbidden');
    }

    if (url.origin !== API_ORIGIN) {
      throw new Error('public_gateway_origin_forbidden');
    }

    if (!isPropertyRoute(url.pathname)) {
      throw new Error('public_gateway_route_forbidden');
    }

    return url.toString();
  }

  function isVerifiedAddressResult(item, body) {
    return !!(
      item &&
      item.source === 'hk_dpo_als' &&
      clean(item.source_provider).toLowerCase() === 'digital policy office' &&
      item.coordinate_verified === true &&
      clean(item.source_ref) &&
      item.mock_data_used !== true &&
      body &&
      body.mock_data_used !== true
    );
  }

  function enforceVerifiedAddressSurface(response) {
    var body = response && response.body && typeof response.body === 'object'
      ? response.body
      : {};
    var allResults = Array.isArray(body.results) ? body.results : [];
    var verifiedResults = allResults.filter(function (item) {
      return isVerifiedAddressResult(item, body);
    });

    return Object.assign({}, response, {
      body: Object.assign({}, body, {
        results: verifiedResults,
        location_provenance_contract: LOCATION_PROVENANCE_CONTRACT,
        resolver_result_count: allResults.length,
        verified_address_result_count: verifiedResults.length,
        address_verification_policy: 'dpo-als-user-selected-only',
        fallback_results_exposed_as_verified: false
      })
    });
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * 2. DATA GATE TRANSPORT PATCHING
   * ────────────────────────────────────────────────────────────────────────── */
  function patchGate() {
    var gate = window.GPROPERTY_DATA_GATE;
    var originalRequest;

    if (!gate || gate.transportVersion === VERSION) {
      return !!gate;
    }

    originalRequest = gate.request;
    if (typeof originalRequest !== 'function') {
      return false;
    }

    function request(endpoint, options, timeoutMs) {
      var resolved;
      var requestOptions;

      try {
        resolved = resolvePublicEndpoint(endpoint);
      } catch (error) {
        return Promise.reject(error);
      }

      requestOptions = Object.assign({}, options || {});
      if (!requestOptions.credentials) {
        requestOptions.credentials = 'same-origin';
      }

      return originalRequest(resolved, requestOptions, timeoutMs);
    }

    function publicReadiness(endpoint) {
      var resolved;
      var url;

      try {
        resolved = resolvePublicEndpoint(endpoint);
        url = new URL(resolved);
      } catch (error) {
        return Promise.reject(error);
      }

      if (url.pathname !== '/property/family-data/status') {
        return Promise.reject(new Error('readiness_endpoint_invalid'));
      }

      return request(resolved, { method: 'GET' });
    }

    function searchLocations(endpoint, query) {
      var term = gate.clean ? gate.clean(query) : clean(query);
      var resolved;
      var url;

      if (term.length < 2) {
        return Promise.reject(new Error('query_too_short'));
      }

      if (term.length > 255) {
        return Promise.reject(new Error('query_too_long'));
      }

      try {
        resolved = resolvePublicEndpoint(endpoint);
        url = new URL(resolved);
      } catch (error) {
        return Promise.reject(error);
      }

      if (url.pathname !== '/property/location/search') {
        return Promise.reject(new Error('location_search_endpoint_invalid'));
      }

      url.searchParams.set('q', term);

      return request(url.toString(), { method: 'GET' }, 10000)
        .then(enforceVerifiedAddressSurface);
    }

    window.GPROPERTY_DATA_GATE = Object.freeze(
      Object.assign({}, gate, {
        transportVersion: VERSION,
        locationProvenanceContract: LOCATION_PROVENANCE_CONTRACT,
        publicGatewayOrigin: API_ORIGIN,
        resolvePublicEndpoint: resolvePublicEndpoint,
        isVerifiedAddressResult: isVerifiedAddressResult,
        request: request,
        publicReadiness: publicReadiness,
        searchLocations: searchLocations
      })
    );

    return true;
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * 3. INITIALIZATION & EVENT BINDINGS
   * ────────────────────────────────────────────────────────────────────────── */
  if (!patchGate()) {
    document.addEventListener('DOMContentLoaded', patchGate, { once: true });
    window.addEventListener('load', patchGate, { once: true });
  }
})(window, document);
